import { useMutation } from "@apollo/client"
import styled from "@emotion/styled"
import { Box, Button, notify, theme } from "@pallet-hq/hegel"
import mixpanel from "mixpanel-browser"
import AnimatedStar from "components/AnimatedStar"
import { BUTTON_TYPES, TRACK_EVENTS } from "constants/events"
import { useCandidateApplication } from "@pallet/features/onboardingL2/useCandidateApplication"
import useAuthContext from "state/useAuthContext"
import { useLikeTeamAction } from "./hooks"
import { LIKE_TEAM_MUTATION } from "./mutations"
import { LikeTeamAction_team } from "__generated__/LikeTeamAction_team"
import { ToggleLikeTeamMutation } from "__generated__/ToggleLikeTeamMutation"

type LikeTeamActionProps = {
    team: LikeTeamAction_team
}

const LikeTeamAction = ({ team }: LikeTeamActionProps) => {
    const { user } = useAuthContext()
    const { showLikeAction, shouldShowApplication, pallet } =
        useLikeTeamAction()
    const { showModal: startCandidateApplication } = useCandidateApplication()
    const [likeTeam] = useMutation<ToggleLikeTeamMutation>(LIKE_TEAM_MUTATION, {
        variables: { team: team.uuid },
    })

    const handleLike = async () => {
        if (shouldShowApplication) {
            mixpanel.track(TRACK_EVENTS.BUTTON_CLICKED, {
                type: BUTTON_TYPES.LIKE_TEAM,
                applylist_slug: pallet,
                user_slug: user?.slug,
                team: team.uuid,
                start_candidate_application: true,
            })
            return startCandidateApplication()
        }
        const resp = await likeTeam({ variables: { liked: !team.isLiked } })
        if (!resp.data?.toggleLikeTeam?.team?.id) {
            notify.fail(
                "Your request failed. Please try again or contact support."
            )
        } else {
            mixpanel.track(TRACK_EVENTS.BUTTON_CLICKED, {
                type: BUTTON_TYPES.LIKE_TEAM,
                applylist_slug: pallet,
                user_slug: user?.slug,
                team: team.uuid,
                liked: resp.data.toggleLikeTeam.team.isLiked,
                start_candidate_application: false,
            })
        }
    }

    if (!showLikeAction) return <></>
    return (
        <StyledButton
            size="small"
            backgroundColor={team.isLiked && "gold000"}
            variant="secondary"
            onClick={handleLike}
        >
            <Box
                display="flex"
                gridGap="xsmall"
                alignItems="center"
                justifyContent="center"
            >
                <AnimatedStar filled={team.isLiked} />
                Interested
            </Box>
        </StyledButton>
    )
}

export { LikeTeamAction }

const StyledButton = styled(Button)`
    &:hover {
        background-color: ${theme.colors.gold100};
        opacity: 0.88;
    }
`
